/**
 * `CohortHeatmap` — retention grid for `/internal/admin/metrics/cohorts`.
 *
 * Rows = signup cohort month (`cohort_month`, with `cohort_size` shown
 * next to the label), columns = months-since-signup (`month_index`),
 * cells colored by retention `pct` on a lime gradient (deeper = higher
 * retention). Empty cells (no data) render as a thin em-dash so the
 * grid stays aligned.
 *
 * The grid width = max(month_index) + 1 across all cohorts (the API
 * returns sparse per-cohort rows; we widen the layout so every cohort
 * gets the same column count and the heatmap is rectangular).
 *
 * A small inline legend across the bottom shows the 0% → 100% scale.
 */

import type { AdminMetricsCohortRow } from '@sill/types';

import { formatCohortMonth, formatCount, formatDays, formatPct } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

/**
 * Map a retention fraction in [0, 1] to a cell background. We use a
 * single-hue lime gradient so the visual jumps to "more retention =
 * more lime" without inventing a second accent color. Light theme
 * inverts to a deeper lime ink on cream-ish surface to stay legible.
 */
function cellStyleForPct(
  pct: number,
  C: ReturnType<typeof useC>,
): { background: string; color: string } {
  // Clamp to [0, 1] — the API guarantees this but we defend.
  const p = Math.max(0, Math.min(1, pct));
  if (p === 0) {
    return { background: C.surface2, color: C.dim };
  }
  if (C.name === 'dark') {
    // Dark: layer increasing opacity of lime onto the surface tier.
    // `0.06` is barely-visible (1% retention) → `0.55` is full saturation
    // at 100% retention. Text flips to dark ink above 35% so the high
    // cells stay readable.
    const alpha = 0.06 + 0.49 * p;
    return {
      background: `rgba(217, 255, 63, ${alpha.toFixed(3)})`,
      color: p > 0.35 ? C.ink : C.text,
    };
  }
  // Light: use the lime ink (#3D5500) at increasing opacity on a
  // light surface. Text stays the deep-lime ink for legibility.
  const alpha = 0.08 + 0.62 * p;
  return {
    background: `rgba(61, 85, 0, ${alpha.toFixed(3)})`,
    color: p > 0.45 ? '#FAF7F0' : C.text,
  };
}

export interface CohortHeatmapProps {
  cohorts: ReadonlyArray<AdminMetricsCohortRow>;
}

export function CohortHeatmap({ cohorts }: CohortHeatmapProps): JSX.Element {
  const C = useC();

  if (cohorts.length === 0) {
    return (
      <div
        className="p-8 text-center"
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.muted,
          background: C.surface,
          border: `1px solid ${C.borderSoft}`,
          borderRadius: 3,
        }}
      >
        No cohorts yet. Retention data accrues once accounts complete a full month.
      </div>
    );
  }

  // Build the rectangular column-set: at minimum one column per
  // month_index we've seen across any cohort.
  const maxMonthIndex = cohorts.reduce((max, row) => {
    const localMax = row.retention.reduce((m, c) => Math.max(m, c.month_index), -1);
    return Math.max(max, localMax);
  }, -1);
  const colCount = maxMonthIndex + 1;
  // Cap visible columns at 18 (≈1.5 years) for screen-share legibility;
  // if any cohort has more, we still render them — but in practice the
  // backfill is bounded by signup history.
  const columns = Array.from({ length: colCount }, (_, i) => i);

  // Quick lookup: cohort_month → month_index → cell.
  const cellLookup = new Map<string, Map<number, { pct: number; active: number }>>();
  for (const row of cohorts) {
    const m = new Map<number, { pct: number; active: number }>();
    for (const cell of row.retention) {
      m.set(cell.month_index, { pct: cell.pct, active: cell.active_accounts });
    }
    cellLookup.set(row.cohort_month, m);
  }

  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 3,
        padding: 16,
        overflowX: 'auto',
      }}
    >
      <table
        style={{
          borderCollapse: 'separate',
          borderSpacing: 2,
          fontFamily: monoFont,
          fontSize: 11,
        }}
      >
        <thead>
          <tr>
            <th
              className="text-left"
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                fontWeight: 500,
                padding: '4px 8px',
                whiteSpace: 'nowrap',
              }}
            >
              Cohort
            </th>
            <th
              className="text-right"
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                fontWeight: 500,
                padding: '4px 8px',
                whiteSpace: 'nowrap',
              }}
            >
              Size
            </th>
            <th
              className="text-right"
              style={{
                fontFamily: monoFont,
                fontSize: 9,
                color: C.muted,
                letterSpacing: 1.5,
                textTransform: 'uppercase',
                fontWeight: 500,
                padding: '4px 8px',
                whiteSpace: 'nowrap',
              }}
              title="Median per-account active lifespan (LAST − FIRST real audit_record) for this cohort"
            >
              Median tenure
            </th>
            {columns.map((i) => (
              <th
                key={`col-${i}`}
                className="text-center"
                style={{
                  fontFamily: monoFont,
                  fontSize: 9,
                  color: C.muted,
                  letterSpacing: 0.5,
                  fontWeight: 500,
                  padding: '4px 0',
                  minWidth: 44,
                }}
              >
                M{i}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {cohorts.map((row) => {
            const lookup = cellLookup.get(row.cohort_month);
            return (
              <tr key={row.cohort_month}>
                <td
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.text,
                    padding: '4px 8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatCohortMonth(row.cohort_month)}
                </td>
                <td
                  className="text-right"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: C.muted,
                    padding: '4px 8px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatCount(row.cohort_size)}
                </td>
                <td
                  className="text-right"
                  style={{
                    fontFamily: monoFont,
                    fontSize: 11,
                    color: row.median_tenure_days === null ? C.dim : C.body,
                    padding: '4px 8px',
                    whiteSpace: 'nowrap',
                  }}
                  title={
                    row.median_tenure_days === null
                      ? 'No real activity in this cohort'
                      : `Median per-account active lifespan for the ${formatCount(row.cohort_size)}-account cohort`
                  }
                >
                  {formatDays(row.median_tenure_days)}
                </td>
                {columns.map((monthIdx) => {
                  const cell = lookup?.get(monthIdx);
                  if (cell === undefined) {
                    return (
                      <td
                        key={`cell-${row.cohort_month}-${monthIdx}`}
                        className="text-center"
                        style={{
                          background: C.surface2,
                          color: C.dim,
                          padding: '6px 0',
                          minWidth: 44,
                          fontFamily: monoFont,
                          fontSize: 10,
                          borderRadius: 2,
                          border: `1px solid ${C.borderSoft}`,
                        }}
                      >
                        —
                      </td>
                    );
                  }
                  const { background, color } = cellStyleForPct(cell.pct, C);
                  return (
                    <td
                      key={`cell-${row.cohort_month}-${monthIdx}`}
                      className="text-center"
                      title={`${formatPct(cell.pct)} (${formatCount(cell.active)} of ${formatCount(row.cohort_size)})`}
                      style={{
                        background,
                        color,
                        padding: '6px 0',
                        minWidth: 44,
                        fontFamily: monoFont,
                        fontSize: 10,
                        borderRadius: 2,
                        border: `1px solid ${C.borderSoft}`,
                        fontWeight: 500,
                      }}
                    >
                      {formatPct(cell.pct, 0)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>

      <CohortLegend />
    </div>
  );
}

function CohortLegend(): JSX.Element {
  const C = useC();
  const stops = [0, 0.25, 0.5, 0.75, 1];
  return (
    <div
      className="flex items-center gap-3 mt-4"
      aria-hidden="true"
    >
      <span
        style={{
          fontFamily: monoFont,
          fontSize: 9,
          color: C.muted,
          letterSpacing: 1.5,
          textTransform: 'uppercase',
        }}
      >
        Retention
      </span>
      <div className="flex" style={{ gap: 2 }}>
        {stops.map((p) => {
          const { background, color } = cellStyleForPct(p, C);
          return (
            <div
              key={`legend-${p}`}
              className="text-center"
              style={{
                background,
                color,
                width: 44,
                padding: '4px 0',
                fontFamily: monoFont,
                fontSize: 10,
                borderRadius: 2,
                border: `1px solid ${C.borderSoft}`,
                fontWeight: 500,
              }}
            >
              {Math.round(p * 100)}%
            </div>
          );
        })}
      </div>
    </div>
  );
}
